'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { AuthState, KeycloakUserInfo, UserInfo } from './types';
import { globalStore } from '@/app/globalStore';
import { isTokenExpired, parseJwtPayload, type TokenPayload } from '@/lib/auth/tokenUtils';
import { refreshTokens } from '@/lib/keycloak/keycloak-auth';
import { getSandboxLogoutUrl } from '@/lib/keycloak/sandbox-implicit-auth';
import { requestSentryNextjsUserIdentity } from '@/lib/sentry/applySentryVersionTags';
const AuthContext = createContext<AuthState | null>(null);
const TOKEN_REFRESH_INTERVAL_MS = 50 * 1000;
interface KeycloakTokenPayload extends TokenPayload {
  preferred_username?: string;
  email?: string;
  realm_access?: {
    roles?: string[];
  };
  resource_access?: Record<string, {
    roles?: string[];
  }>;
}
function parseUserFromToken(accessToken: string): KeycloakUserInfo | null {
  const parsed = parseJwtPayload<KeycloakTokenPayload>(accessToken);
  if (!parsed || !parsed.sub) {
    return null;
  }
  const clientId = process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID ?? '';
  const realmRoles = parsed.realm_access?.roles ?? [];
  const clientRoles = parsed.resource_access?.[clientId]?.roles ?? [];
  const sandboxUserId = parsed.sub.split(':').pop() ?? parsed.sub;
  return {
    sub: parsed.sub,
    sandboxUserId,
    username: parsed.preferred_username ?? parsed.sub,
    email: parsed.email ?? null,
    roles: [...realmRoles, ...clientRoles]
  };
}
export function KeycloakAuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<KeycloakUserInfo | null>(null);
  const initStarted = useRef(false);
  const updateAuthState = useCallback((token: string | null) => {
    if (token) {
      const userInfo = parseUserFromToken(token);
      setUser(userInfo);
      setAccessToken(token);
      if (userInfo) {
        globalStore.setCurrentUser(userInfo.sub, {
          userName: userInfo.username,
          email: userInfo.email,
          imageUrl: null,
          fullName: userInfo.username
        });
        requestSentryNextjsUserIdentity(userInfo.sub, userInfo.username);
      }
    } else {
      setUser(null);
      setAccessToken(null);
      globalStore.setCurrentUser(null, null);
      requestSentryNextjsUserIdentity(null, null);
    }
  }, []);
  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;
    const init = async () => {
      try {
        const result = await refreshTokens();
        if (result.success) {
          updateAuthState(result.tokens.access_token);
        } else {
          const localStorageToken = localStorage.getItem('keycloak_access_token');
          if (localStorageToken) {
            updateAuthState(localStorageToken);
          } else {
            const response = await fetch('/api/auth/check-token', {
              method: 'GET',
              credentials: 'include'
            });
            if (response.ok) {
              const data = await response.json();
              if (data.accessToken) {
                updateAuthState(data.accessToken);
              } else {
                updateAuthState(null);
              }
            } else {
              updateAuthState(null);
            }
          }
        }
      } catch (e) {
        console.error('[auth] init failed', e);
        updateAuthState(null);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [updateAuthState]);
  useEffect(() => {
    if (!accessToken) return;
    const interval = setInterval(async () => {
      if (isTokenExpired(accessToken, 70)) {
        try {
          const result_0 = await refreshTokens();
          if (result_0.success) {
            updateAuthState(result_0.tokens.access_token);
          } else {
            // If refresh fails (no refresh token in implicit flow), check token expiry
            if (isTokenExpired(accessToken, 0)) {
              // Token is expired, clear auth and redirect to login
              console.warn('[auth] Access token expired and no refresh token available. Please sign in again.');
              updateAuthState(null);
            }
          }
        } catch (e_0) {
          console.error('[auth] token refresh error:', e_0);
        }
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [accessToken, updateAuthState]);
  const login = useCallback(async (redirectUri?: string) => {
    window.location.href = redirectUri || '/auth/signin';
  }, []);
  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      const idToken = localStorage.getItem('keycloak_id_token');
      localStorage.removeItem('keycloak_access_token');
      localStorage.removeItem('keycloak_id_token');
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      updateAuthState(null);
      if (idToken) {
        const logoutUrl = getSandboxLogoutUrl(`${window.location.origin}/auth/signin`, idToken);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = logoutUrl;
        document.body.appendChild(iframe);
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 2000);
      }
      window.location.href = '/auth/signin';
    } catch (error) {
      console.error('[auth] Logout error:', error);
      window.location.href = '/auth/signin';
    } finally {
      setIsLoggingOut(false);
    }
  }, [updateAuthState]);
  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!accessToken || isTokenExpired(accessToken, 30)) {
      const result_1 = await refreshTokens();
      if (result_1.success) {
        setAccessToken(result_1.tokens.access_token);
        return result_1.tokens.access_token;
      }
      throw new Error('[auth] token refresh failed');
    }
    return accessToken;
  }, [accessToken]);
  const userInfo_0: UserInfo | null = user ? {
    userName: user.username,
    email: user.email,
    imageUrl: null,
    fullName: user.username
  } : null;
  const value: AuthState = {
    isLoading,
    isAuthenticated: !!user,
    isLoggingOut,
    user,
    firebaseUser: user,
    userInfo: userInfo_0,
    getAccessToken,
    login,
    logout,
    isLoggingIn: isLoading,
    tokenVerified: !!user,
    isAuthReady: !isLoading
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function useKeycloakAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useKeycloakAuth must be used within KeycloakAuthProvider");
  }
  return ctx;
}
export { AuthContext as KeycloakAuthContext };